import * as d3 from 'd3';
import { geoMercator, geoPath } from 'd3-geo';
import worldGeoJson from './data/world.json';

document.addEventListener('alpine:init', () => {
    Alpine.data('ellisMap', () => ({
        mapContainer: null,
        svg: null,
        g: null,
        zoom: null,
        projection: null,
        path: null,
        tooltip: null,
        currentScale: 1,
        unitsData: [],
        
        init() {
            this.$nextTick(() => {
                this.loadUnitsData().then(() => {
                    this.initMap();
                });
            });
        },
        
        async loadUnitsData() {
            try {
                const response = await fetch('/api/map/units');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                this.unitsData = await response.json();
            } catch (error) {
                console.error('Failed to load units data:', error);               
            }
        },
        
        initMap() {
            this.mapContainer = this.$refs.mapContainer;
            const width = this.mapContainer.clientWidth;
            const height = this.mapContainer.clientHeight;
            
            // Create SVG
            this.svg = d3.select(this.$refs.mapSvg)
                .attr('width', '100%')
                .attr('height', '100%')
                .attr('viewBox', [0, 0, width, height]);
                
            this.g = this.svg.append('g');
            
            // Set up projection
            this.projection = geoMercator()
                .center([19, 45])
                .scale(width / 1.5)
                .translate([width / 2, height / 2]);

            this.path = geoPath().projection(this.projection);
            
            // Filter European and Asian countries
            const eurasianCountries = worldGeoJson.features.filter(d => {
                const continent = this.getContinentByCountry(d.properties.name);
                return continent === 'Europe' || continent === 'Asia';
            });
            
            // Draw base map
            this.g.selectAll('path')
                .data(eurasianCountries)
                .join('path')
                .attr('d', this.path)
                .attr('class', d => {
                    const continent = this.getContinentByCountry(d.properties.name);
                    if (continent === 'Europe') {
                        return 'country europe';
                    } else if (continent === 'Asia') {
                        return 'country asia hidden';
                    } else {
                        return 'country other hidden';
                    }
                });
                
            // Create tooltip
            this.tooltip = d3.select(this.mapContainer)
                .append('div')
                .attr('class', 'map-tooltip')
                .style('opacity', 0);
            
            // Add ELLIS unit markers
            this.g.selectAll('circle')
                .data(this.unitsData)
                .join('circle')
                .attr('cx', d => this.projection([d.longitude, d.latitude])[0])
                .attr('cy', d => this.projection([d.longitude, d.latitude])[1])
                .attr('r', 3)
                .attr('class', 'unit-marker')
                .style('cursor', d => (d.url || d.website) ? 'pointer' : 'default')
                .on('click', (event, d) => {
                    // Handle click to navigate to URL
                    const targetUrl = d.url || d.website;
                    if (targetUrl) {
                        // Prevent event bubbling to avoid zoom/pan interference
                        event.stopPropagation();
                        
                        // Open URL in same window
                        window.location.href = targetUrl;
                        
                        // Alternative: Open in new tab/window
                        // window.open(targetUrl, '_blank', 'noopener,noreferrer');
                    }
                })
                .on('mouseover', (event, d) => {
                    const containerRect = this.mapContainer.getBoundingClientRect();
                    const x = event.clientX - containerRect.left + 10;
                    const y = event.clientY - containerRect.top - 28;
                    
                    const name = d.name || '';
                    const location = d.location || '';
                    const description = d.description ? `<br/>${d.description}` : '';
                    
                    this.tooltip.transition()
                        .duration(200)
                        .style('opacity', .9);
                    this.tooltip.html(`
                        <strong>${name}</strong>
                        ${location ? `<br/>${location}` : ''}
                        ${description ? `<br/>${d.description}` : ''}
                    `)
                        .style('left', `${x}px`)
                        .style('top', `${y}px`);
                        
                    // Increase marker size on hover
                    d3.select(event.currentTarget).attr('r', 6 / this.currentScale);
                })
                .on('mouseout', (event) => {
                    this.tooltip.transition()
                        .duration(500)
                        .style('opacity', 0);
                        
                    // Reset marker size
                    d3.select(event.currentTarget).attr('r', 3 / this.currentScale);
                });
        
            // Initialize zoom and pan behavior
            this.zoom = d3.zoom()
                .scaleExtent([0.5, 4])
                .on('zoom', (event) => {
                    this.g.attr('transform', event.transform);
                    this.currentScale = event.transform.k;
                    
                    // Adjust marker size inversely to zoom scale
                    this.g.selectAll('.unit-marker')
                        .attr('r', 3 / event.transform.k);
                });
        
            this.svg.call(this.zoom);
        },
        
        zoomIn() {
            this.handleZoom(1.2);
        },
        
        zoomOut() {
            this.handleZoom(0.8);
        },
        
        handleZoom(factor) {
            const transform = d3.zoomTransform(this.svg.node());
            const newScale = transform.k * factor;
            
            if (newScale >= 0.5 && newScale <= 4) {
                this.svg.transition()
                    .duration(300)
                    .call(this.zoom.scaleTo, newScale);
            }
        },
        
        getContinentByCountry(countryName) {
            const europeanCountries = [
                'France', 'Germany', 'Spain', 'Italy', 'England', 
                'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Denmark',
                'Sweden', 'Norway', 'Finland', 'Poland', 'Czech Republic',
                'Slovakia', 'Hungary', 'Romania', 'Bulgaria', 'Greece',
                'Portugal', 'Ireland', 'Estonia', 'Latvia', 'Iceland',
                'Lithuania', 'Belarus', 'Ukraine', 'Moldova', 'Croatia',
                'Slovenia', 'Republic of Serbia', 'Bosnia and Herzegovina', 'Albania',
                'Macedonia', 'Montenegro', 'Luxembourg', 'Malta',
                'Israel', 'Kosovo', 'Turkey',
            ];
            return europeanCountries.includes(countryName) ? 'Europe' : 'Asia';
        }
    }));
});
